import type { ReactNode } from "react";
import { Toggle } from "@/components/ui/toggle.tsx";

type SettingsRowBase = {
  title: string;
  description?: string;
  hint?: string;
};

/**
 * Either the row supplies its own trailing control, or it describes a switch
 * and this component builds one. Never both: a union rather than optional
 * props, so a row can't ask for two controls and render whichever branch is
 * checked first.
 */
type SettingsRowProps = SettingsRowBase &
  (
    | {
        control: ReactNode;
        checked?: never;
        onCheckedChange?: never;
        disabled?: never;
      }
    | {
        control?: never;
        checked: boolean;
        onCheckedChange: (checked: boolean) => void;
        disabled?: boolean;
      }
  );

/**
 * One labelled row in a settings list: title, optional description and hint,
 * and a control on the right.
 *
 * Most rows are switches, so those pass `checked`/`onCheckedChange` and get a
 * correctly sized, correctly labelled `Toggle` without restating it — the
 * switch is built here so its size can't drift row to row. Rows whose control
 * is something else pass `control` instead.
 *
 * This replaced two components that had grown identical wrappers: a `SwitchRow`
 * for the switch rows and a `ToggleRow` for the rest, which meant the same row
 * had two definitions of its padding, divider, and text sizes.
 */
export const SettingsRow = ({
  title,
  description,
  hint,
  control,
  checked,
  onCheckedChange,
  disabled,
}: SettingsRowProps) => (
  <div className="flex items-center justify-between gap-4 py-4 border-b border-border last:border-b-0">
    <div className="flex-1 min-w-0">
      <div className="text-sm font-medium">{title}</div>
      {description && <div className="text-sm text-muted-foreground mt-0.5">{description}</div>}
      {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
    </div>
    {control ?? (
      // `md` is the settings switch size: level with the 24px status pills,
      // where `default`'s 44px track outweighs a settings row.
      <Toggle
        size="md"
        checked={checked ?? false}
        onCheckedChange={onCheckedChange ?? (() => {})}
        disabled={disabled}
        aria-label={title}
      />
    )}
  </div>
);
